import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client'; // still needed for auth.getSession / signIn / signOut
import {
  validateInvitationToken,
  acceptInvitationWithPassword,
  currentUserHasAnyMembership,
} from '@/services/invitations.service';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

type State =
  | { kind: 'loading' }
  | { kind: 'valid'; email: string; role: 'admin' | 'atendente' | 'prestador'; orgName: string; inviterName: string }
  | { kind: 'existing_user'; email: string }
  | { kind: 'expired' }
  | { kind: 'revoked' }
  | { kind: 'invalid_token' }
  | { kind: 'already_accepted' }
  | { kind: 'logged_in_wrong_org'; email: string };

export default function AceitarConvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) {
        setState({ kind: 'invalid_token' });
        return;
      }

      // If already logged in AND user has any existing memberships, block acceptance
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session?.user) {
        const hasMembership = await currentUserHasAnyMembership(sessionData.session.user.id);
        if (hasMembership) {
          setState({
            kind: 'logged_in_wrong_org',
            email: sessionData.session.user.email ?? '',
          });
          return;
        }
      }

      const data = await validateInvitationToken(token);
      if (!data) {
        setState({ kind: 'invalid_token' });
        return;
      }
      const s = data.state as string;
      switch (s) {
        case 'valid':
          setState({
            kind: 'valid',
            email: data.email as string,
            role: data.role as 'admin' | 'atendente' | 'prestador',
            orgName: data.org_name as string,
            inviterName: data.inviter_name as string,
          });
          break;
        case 'existing_user':
          setState({ kind: 'existing_user', email: data.email as string });
          break;
        case 'expired':
        case 'revoked':
        case 'invalid_token':
        case 'already_accepted':
          setState({ kind: s });
          break;
        default:
          setState({ kind: 'invalid_token' });
      }
    })();
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state.kind !== 'valid') return;
    if (password !== confirmPassword) {
      toast.error(t('invite.accept.valid.passwordMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const data = await acceptInvitationWithPassword({ token: token!, password });
      // Auto-login then redirect
      await supabase.auth.signInWithPassword({ email: state.email, password });
      toast.success(t('invite.accept.acceptedToast', { orgName: state.orgName }));
      const role = data.role as string;
      navigate(role === 'prestador' ? '/meus-servicos' : '/painel');
    } catch (e: unknown) {
      const code = (e as Error).message || 'GENERIC';
      toast.error(
        t(`invite.accept.errors.${code}`, { defaultValue: t('invite.accept.errors.GENERIC') }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (state.kind === 'loading') {
    return (
      <CenteredCard>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="ml-2">{t('invite.accept.loading')}</span>
        </CardContent>
      </CenteredCard>
    );
  }

  if (state.kind === 'logged_in_wrong_org') {
    return (
      <CenteredCard>
        <CardHeader>
          <CardTitle>{t('invite.accept.loggedInWrongOrg.title')}</CardTitle>
          <CardDescription>
            {t('invite.accept.loggedInWrongOrg.body', { email: state.email })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.reload();
            }}
          >
            {t('invite.accept.loggedInWrongOrg.logoutButton')}
          </Button>
        </CardContent>
      </CenteredCard>
    );
  }

  if (state.kind === 'existing_user') {
    return (
      <CenteredCard>
        <CardHeader>
          <CardTitle>{t('invite.accept.existingUser.title')}</CardTitle>
          <CardDescription>{t('invite.accept.existingUser.subtitle')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate(`/auth?redirect=/aceitar-convite/${token}`)}>
            {t('invite.accept.existingUser.loginButton')}
          </Button>
        </CardContent>
      </CenteredCard>
    );
  }

  if (
    state.kind === 'expired' ||
    state.kind === 'revoked' ||
    state.kind === 'invalid_token' ||
    state.kind === 'already_accepted'
  ) {
    const keyMap: Record<string, string> = {
      expired: 'expired',
      revoked: 'revoked',
      invalid_token: 'invalidToken',
      already_accepted: 'alreadyAccepted',
    };
    const key = keyMap[state.kind];
    return (
      <CenteredCard>
        <CardHeader>
          <CardTitle>{t(`invite.accept.${key}.title`)}</CardTitle>
          <CardDescription>{t(`invite.accept.${key}.body`)}</CardDescription>
        </CardHeader>
        {state.kind === 'already_accepted' && (
          <CardContent>
            <Button onClick={() => navigate('/painel')}>
              {t('invite.accept.alreadyAccepted.goToApp')}
            </Button>
          </CardContent>
        )}
      </CenteredCard>
    );
  }

  // state.kind === 'valid'
  return (
    <CenteredCard>
      <CardHeader>
        <CardTitle>{t('invite.accept.valid.title', { orgName: state.orgName })}</CardTitle>
        <CardDescription>
          {t('invite.accept.valid.subtitle', {
            inviterName: state.inviterName,
            roleLabel: t(`invite.dialog.roles.${state.role}`),
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>{t('invite.accept.valid.emailLabel')}</Label>
            <Input value={state.email} disabled />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">{t('invite.accept.valid.passwordLabel')}</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('invite.accept.valid.passwordHint')}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">
              {t('invite.accept.valid.confirmPasswordLabel')}
            </Label>
            <Input
              id="confirm-password"
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? t('invite.accept.valid.submitting') : t('invite.accept.valid.submit')}
          </Button>
        </form>
      </CardContent>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/30">
      <Card className="w-full max-w-md">{children}</Card>
    </div>
  );
}
